import {
  MAX_INSTRUCTION_LENGTH,
  MAX_INSTRUCTIONS_PER_REWRITE,
  type MenuSetting,
  type RewriteOptions,
  type SavedInstruction,
} from '@lyrmo/shared';
import { matchesShortcut } from '@lyrmo/shared/shortcuts';
import { useId, useState } from 'react';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { RollingNumber } from '../../components/RollingNumber';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { useShortcuts } from '../shortcuts/useShortcuts';
import { instructionItems, matches, menuSections, type MenuItem } from './menu-items';
import { MenuCheckRow, MenuFooter, MenuSearch, MenuSection, type MenuAction } from './MenuPanel';
import { moveWithArrows, usePopover, useSearchFocus } from './usePopover';

interface InstructionMenuProps {
  readonly value: RewriteOptions;
  readonly onChange: (value: RewriteOptions) => void;
  /** The writer's saved instructions; null until they're known. */
  readonly saved: readonly SavedInstruction[] | null;
  /** Which instructions the writer hid or pinned; null until known. */
  readonly settings?: readonly MenuSetting[] | null | undefined;
  /** Opens Manage instructions, to hide, pin, save, change, or delete them. */
  readonly onManage?: (() => void) | undefined;
  /**
   * Starts a new saved instruction, which the rewrites then follow: from the instruction typed
   * for this rewrite, or from nothing.
   */
  readonly onNew?: ((text: string) => void) | undefined;
  /** Back to the draft, once done here; the menu's button takes focus otherwise. */
  readonly onDone?: (() => void) | undefined;
}

// No saved instructions is no list at all, so the options stay as they'd be without any.
const listOrNone = (ids: readonly string[]) => (ids.length > 0 ? ids : undefined);

// The counter turns to the warning colour this close to the most an instruction can be.
const NEARLY_FULL = 0.9;

/**
 * Instruction, in the editor's toolbar: a menu of the writer's saved instructions, laid out like
 * AI tools, any of which the rewrites follow, the pinned ones first and those they hid left out,
 * apart from the ones followed; then the instruction for this rewrite alone, which can be saved.
 */
export function InstructionMenu({
  value,
  onChange,
  saved,
  settings,
  onManage,
  onNew,
  onDone,
}: InstructionMenuProps) {
  const { t, format } = useI18n();
  const { platform, shortcuts } = useShortcuts();
  const { open, shown, present, leaving, setOpen, rootRef, triggerRef, onBlur } = usePopover();
  const searchRef = useSearchFocus(shown && saved !== null && saved.length > 0);
  const [query, setQuery] = useState('');
  const panelId = useId();
  const onceId = useId();
  const chosen = value.savedInstructions ?? [];
  const full = chosen.length >= MAX_INSTRUCTIONS_PER_REWRITE;
  const typed = value.instruction ?? '';
  // Every instruction the rewrites follow, the one typed for this rewrite included.
  const followed = chosen.length + (typed.trim() ? 1 : 0);
  const items = instructionItems(saved ?? [], settings);
  const sections = menuSections(items, (item) => chosen.includes(item.id));
  const found = (list: readonly MenuItem[]) => list.filter((item) => matches(item, query));
  const pinned = found(sections.pinned);
  const rest = found(sections.own);

  const set = (changes: Partial<RewriteOptions>) => {
    onChange({ ...value, ...changes });
  };

  /**
   * Done with the menu: it closes, and writing carries on in the draft. Typing an instruction has
   * no choice to close it, as picking a tone does, so Enter or Done does.
   */
  const done = () => {
    setOpen(false);
    if (onDone) onDone();
    else triggerRef.current?.focus();
  };

  /** Closes the menu for something that opens elsewhere, such as the instructions dialog. */
  const leave = (then: () => void) => () => {
    setOpen(false);
    then();
  };

  const list = (choices: readonly MenuItem[]) => (
    <ul className="menu-list">
      {choices.map((item) => {
        const on = chosen.includes(item.id);
        return (
          <MenuCheckRow
            key={item.id}
            item={item}
            checked={on}
            disabled={!on && full}
            onChange={(checked) => {
              set({
                savedInstructions: listOrNone(
                  checked ? [...chosen, item.id] : chosen.filter((id) => id !== item.id),
                ),
              });
            }}
          />
        );
      })}
    </ul>
  );

  const tally = saved !== null && saved.length > 0 && (
    <span className={cx('menu-tally', full && 'is-full')}>
      <RollingNumber value={format.number(chosen.length)} /> /{' '}
      {format.number(MAX_INSTRUCTIONS_PER_REWRITE)}
    </span>
  );

  const actions: MenuAction[] = [];
  if (onNew) {
    actions.push({
      label: t.instructions.newInstruction,
      icon: 'plus',
      onClick: leave(() => {
        onNew('');
      }),
      accent: true,
    });
  }
  if (onManage) {
    actions.push({ label: t.options.manageInstructions, icon: 'edit', onClick: leave(onManage) });
  }

  return (
    <div className="instruction-menu" ref={rootRef} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className={cx('toolbar-button instruction-trigger', followed > 0 && 'is-set')}
        aria-label={t.options.instruction}
        title={t.options.instruction}
        aria-expanded={shown}
        aria-controls={panelId}
        onClick={() => {
          if (!open) setQuery('');
          setOpen((current) => !current);
        }}
      >
        <Icon name="sparkle" />
        <span className="toolbar-button-text">{t.options.instruction}</span>
        {/* The chips below name them; this is how many, for a glance. */}
        {followed > 0 && (
          <span className="instruction-count" aria-hidden="true">
            <RollingNumber value={format.number(followed)} />
          </span>
        )}
        <span className="instruction-chevron" aria-hidden="true">
          <Icon name="chevron" />
        </span>
      </button>
      {present && (
        <div
          className={cx('menu-panel instruction-panel', leaving && 'is-leaving')}
          id={panelId}
          inert={leaving}
          role="dialog"
          aria-label={t.options.instruction}
          onKeyDown={(event) => {
            // The rewrite's own shortcut goes on to start it, with the menu out of its way.
            if (matchesShortcut(event, shortcuts.rewrite, platform)) done();
            else moveWithArrows(event);
          }}
        >
          {saved !== null && saved.length > 0 && (
            <div className="menu-top">
              <MenuSearch inputRef={searchRef} value={query} onChange={setQuery} />
            </div>
          )}

          <div className="menu-body">
            {pinned.length > 0 && (
              <MenuSection label={t.menus.pinned} aside={tally}>
                {list(pinned)}
              </MenuSection>
            )}
            <MenuSection
              label={t.options.savedInstructions}
              aside={pinned.length > 0 ? undefined : tally}
            >
              {saved === null ? (
                <p className="menu-empty" role="status">
                  {t.options.savedLoading}
                </p>
              ) : saved.length === 0 ? (
                <p className="menu-empty">{t.options.noSaved}</p>
              ) : (
                rest.length > 0 && list(rest)
              )}
              {query.trim() && pinned.length + rest.length === 0 && (
                <p className="menu-empty" role="status">
                  {t.menus.noMatch(query.trim())}
                </p>
              )}
              {full && (
                <p className="menu-note">{t.options.atMost(MAX_INSTRUCTIONS_PER_REWRITE)}</p>
              )}
            </MenuSection>

            <div className="menu-section instruction-once-section">
              <label className="menu-heading instruction-once-label" htmlFor={onceId}>
                {t.options.justThisOnce}
              </label>
              <textarea
                id={onceId}
                className="instruction-once"
                rows={2}
                placeholder={t.options.instructionPlaceholder}
                maxLength={MAX_INSTRUCTION_LENGTH}
                value={typed}
                aria-keyshortcuts="Enter"
                onChange={(event) => {
                  set({ instruction: event.target.value || undefined });
                }}
                onKeyDown={(event) => {
                  // Enter is done, as one line is nearly always enough; Shift+Enter breaks a line.
                  const modified = event.shiftKey || event.altKey || event.ctrlKey || event.metaKey;
                  if (event.key !== 'Enter' || modified || event.nativeEvent.isComposing) return;
                  event.preventDefault();
                  done();
                }}
              />
              {typed && (
                <div className="instruction-once-footer">
                  {onNew && typed.trim() && (
                    <Button
                      className="instruction-save"
                      onClick={leave(() => {
                        onNew(typed.trim());
                      })}
                    >
                      {t.options.saveForLater}
                    </Button>
                  )}
                  <span
                    className={cx(
                      'instruction-counter',
                      typed.length >= MAX_INSTRUCTION_LENGTH * NEARLY_FULL && 'is-near',
                    )}
                    aria-hidden="true"
                  >
                    {format.number(typed.length)} / {format.number(MAX_INSTRUCTION_LENGTH)}
                  </span>
                  <Button
                    variant="primary"
                    className="instruction-done"
                    shortcut="↵"
                    onClick={done}
                  >
                    {t.common.done}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {actions.length > 0 && <MenuFooter hidden={sections.hidden} actions={actions} />}
        </div>
      )}
    </div>
  );
}

interface InstructionChipsProps {
  readonly value: RewriteOptions;
  readonly onChange: (value: RewriteOptions) => void;
  readonly saved: readonly SavedInstruction[] | null;
}

/** The instructions the rewrites follow, each with a button to stop following it. */
export function InstructionChips({ value, onChange, saved }: InstructionChipsProps) {
  const { t } = useI18n();
  const chosen = value.savedInstructions ?? [];
  if (chosen.length === 0 && !value.instruction) return null;
  return (
    <ul className="instruction-chips" aria-label={t.options.activeInstructions}>
      {chosen.map((id) => {
        // Until the instructions load, a chosen one is named only as a saved instruction.
        const instruction = saved?.find((other) => other.id === id);
        const label = instruction?.name ?? t.options.savedInstruction;
        return (
          <li key={id} className="instruction-chip">
            <span className="instruction-chip-mark" aria-hidden="true">
              <Icon name="sparkle" />
            </span>
            <span className="instruction-chip-text" title={instruction?.text}>
              {label}
            </span>
            <button
              type="button"
              className="instruction-chip-remove"
              aria-label={t.options.stopFollowing(label)}
              title={t.options.stopFollowing(label)}
              onClick={() => {
                onChange({
                  ...value,
                  savedInstructions: listOrNone(chosen.filter((other) => other !== id)),
                });
              }}
            >
              <Icon name="close" />
            </button>
          </li>
        );
      })}
      {value.instruction && (
        <li className="instruction-chip instruction-chip--once">
          <span className="instruction-chip-mark" aria-hidden="true">
            <Icon name="edit" />
          </span>
          <span className="instruction-chip-text" title={value.instruction}>
            {value.instruction}
          </span>
          <button
            type="button"
            className="instruction-chip-remove"
            aria-label={t.options.removeInstruction}
            title={t.options.removeInstruction}
            onClick={() => {
              onChange({ ...value, instruction: undefined });
            }}
          >
            <Icon name="close" />
          </button>
        </li>
      )}
    </ul>
  );
}
